import { OCR_CONFIG } from '../../config/ocr.config';
import { RawOCRResponse, NormalizedOCRDocument } from './ocrTypes';
import { normalizeOCRContent } from './ocrNormalizer';

export interface OCRRequestOptions {
  fileName?: string;
  fileSize?: number;
  onProgress?: (status: string) => void;
  signal?: AbortSignal;
}

/**
 * Converts a browser File or Blob into a Base64 Data URL string
 */
export async function fileToBase64(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to encode image to base64 Data URL.'));
      }
    };
    reader.onerror = () => reject(reader.error || new Error('FileReader error.'));
    reader.readAsDataURL(file);
  });
}

/**
 * Converts any image input (File, Blob, base64 data URL) into a binary Blob
 */
function toImageBlob(imageInput: File | Blob | string, fallbackMime = 'image/png'): Blob {
  if (imageInput instanceof Blob) {
    return imageInput;
  }
  if (typeof imageInput === 'string') {
    if (imageInput.startsWith('data:')) {
      const parts = imageInput.split(',');
      const mime = parts[0].split(':')[1]?.split(';')[0] || fallbackMime;
      const byteString = atob(parts[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: mime });
    } else {
      const byteString = atob(imageInput);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      return new Blob([ab], { type: fallbackMime });
    }
  }
  return new Blob([], { type: fallbackMime });
}

/**
 * Executes OCR request against configured API with multipart FormData & JSON fallback
 */
export async function executeOCR(
  imageInput: File | Blob | string,
  options?: OCRRequestOptions
): Promise<NormalizedOCRDocument> {
  const startTime = Date.now();
  const fileBlob = toImageBlob(imageInput);
  const fileName = options?.fileName || (imageInput instanceof File ? imageInput.name : 'scan.png');

  // File size safeguard
  if (fileBlob.size > OCR_CONFIG.limits.maxFileSizeBytes) {
    throw new Error(
      `Image size (${(fileBlob.size / (1024 * 1024)).toFixed(1)} MB) exceeds limit of ${OCR_CONFIG.limits.maxFileSizeBytes / (1024 * 1024)} MB.`
    );
  }

  let lastError: Error | null = null;
  const maxAttempts = OCR_CONFIG.limits.maxRetries + 1;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      options?.onProgress?.(
        attempt > 1 ? `Retrying OCR request (${attempt}/${maxAttempts})...` : 'Scanning image with OCR...'
      );

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), OCR_CONFIG.limits.timeoutMs);

      if (options?.signal) {
        options.signal.addEventListener('abort', () => controller.abort());
      }

      // Prepare Base64 Data URL
      const base64Data = await fileToBase64(fileBlob);
      const jsonPayload = {
        files: [base64Data],
        image: base64Data,
        file: base64Data,
      };

      const response = await fetch(OCR_CONFIG.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json, text/plain, */*',
        },
        body: JSON.stringify(jsonPayload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        if (response.status === 401 || response.status === 403) {
          throw new Error('OCR Service authentication failed. Please check credentials.');
        }
        if (response.status === 429) {
          throw new Error('OCR Service rate limit reached. Please wait a moment and try again.');
        }
        if (response.status === 413) {
          throw new Error('Image payload too large for the OCR server.');
        }
        throw new Error(
          `OCR Service responded with error HTTP ${response.status}: ${errorText.substring(0, 120)}`
        );
      }

      const responseData: RawOCRResponse = await response.json();
      const durationMs = Date.now() - startTime;

      options?.onProgress?.('Parsing recognized mathematical formulas and tables...');

      return normalizeOCRContent(responseData, {
        fileName,
        fileSize: fileBlob.size,
        durationMs,
      });
    } catch (err: any) {
      if (err.name === 'AbortError') {
        throw new Error('OCR request timed out after 30 seconds. Please try with a smaller image or retry.');
      }

      lastError = err instanceof Error ? err : new Error(String(err));

      if (lastError.message.includes('401') || lastError.message.includes('403') || lastError.message.includes('413')) {
        throw lastError;
      }

      if (attempt < maxAttempts) {
        await new Promise((res) => setTimeout(res, attempt * 1000));
      }
    }
  }

  throw lastError || new Error('OCR processing failed.');
}
